import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { Network } from "lucide-react";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import { fetchOrganizationHierarchy } from "../../../Apis/OrganizationLevel-api";
import "./organizationHierarchy.css";

function resolveImageUrl(imagePath) {
  if (!imagePath || imagePath === "-" || imagePath === "N/A") return "";
  const raw = String(imagePath).trim();
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    return raw;
  }
  if (raw.startsWith("/storage/app/public")) {
    return `${MAIN_URL}${raw}`;
  }
  if (raw.startsWith("storage/")) {
    return `${MAIN_URL}/${raw}`;
  }
  if (
    raw.startsWith("employees/") ||
    raw.startsWith("persons/") ||
    raw.startsWith("interns/")
  ) {
    return `${MAIN_URL}/storage/app/public/${raw}`;
  }
  return `${MAIN_URL}/storage/app/public/employees/${raw.replace(/^\/+/, "")}`;
}

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function normalizeEmploymentType(name) {
  const value = String(name || "")
    .trim()
    .toLowerCase();
  if (!value) return "";
  if (value.includes("freelance")) return "freelancer";
  if (value === "employee" || value.includes("employee")) return "employee";
  return value;
}

function typeIdsForKey(employmentTypes, key) {
  return new Set(
    (employmentTypes || [])
      .filter((type) => normalizeEmploymentType(type.employment_type_name) === key)
      .map((type) => String(type.organization_employment_type_id))
  );
}

function isFreelancerNode(node, freelancerTypeIds) {
  if (node?.is_shared_resource) return false;
  if (node?.is_freelancer) return true;
  if (normalizeEmploymentType(node?.employment_type_name) === "freelancer") return true;
  const typeId = node?.organization_employment_type_id;
  return typeId != null && freelancerTypeIds.has(String(typeId));
}

function isEmployeeNode(node, employeeTypeIds, freelancerTypeIds) {
  if (node?.is_shared_resource) return false;
  if (isFreelancerNode(node, freelancerTypeIds)) return false;
  if (normalizeEmploymentType(node?.employment_type_name) === "employee") return true;
  const typeId = node?.organization_employment_type_id;
  if (typeId != null && employeeTypeIds.has(String(typeId))) return true;
  // Regular staff with no freelance type still count as employees.
  return !node?.employment_type_name;
}

function nodeMatchesPersonType(node, personType, employeeTypeIds, freelancerTypeIds) {
  if (personType === "all") return true;
  if (personType === "shared_resource") return Boolean(node?.is_shared_resource);
  if (personType === "freelancer") return isFreelancerNode(node, freelancerTypeIds);
  if (personType === "employee") {
    return isEmployeeNode(node, employeeTypeIds, freelancerTypeIds);
  }
  return String(node?.organization_employment_type_id ?? "") === String(personType);
}

function flattenHierarchy(nodes, acc = []) {
  (nodes || []).forEach((node) => {
    acc.push(node);
    flattenHierarchy(node.children, acc);
  });
  return acc;
}

function filterTreeByPersonType(
  nodes,
  personType,
  employeeTypeIds,
  freelancerTypeIds
) {
  if (personType === "all") return nodes || [];

  const original = flattenHierarchy(nodes);
  const originalByPersonId = new Map();
  original.forEach((node) => {
    if (node.organization_person_id) {
      originalByPersonId.set(Number(node.organization_person_id), node);
    }
  });

  const matching = new Map();
  original.forEach((node) => {
    if (nodeMatchesPersonType(node, personType, employeeTypeIds, freelancerTypeIds)) {
      matching.set(hierarchyNodeKey(node), { ...node, children: [] });
    }
  });

  const roots = [];
  matching.forEach((clone) => {
    let managerId = clone.reporting_manager_id
      ? Number(clone.reporting_manager_id)
      : null;
    let attached = false;
    const seen = new Set();

    while (managerId && !seen.has(managerId)) {
      seen.add(managerId);
      const parent = originalByPersonId.get(managerId);
      if (!parent) break;
      const parentKey = hierarchyNodeKey(parent);
      const matchingParent = matching.get(parentKey);
      if (matchingParent) {
        matchingParent.children.push(clone);
        attached = true;
        break;
      }
      managerId = parent.reporting_manager_id
        ? Number(parent.reporting_manager_id)
        : null;
    }

    if (!attached) {
      roots.push(clone);
    }
  });

  return roots;
}

function hierarchyNodeKey(person) {
  if (person?.is_shared_resource && person?.organization_user_id) {
    return `sr-${person.organization_user_id}`;
  }
  return (
    person?.organization_person_id ??
    person?.employee_id ??
    person?.organization_user_id ??
    person?.name
  );
}

/** Managers with sub-teams go to far left & right; individual contributors stay in the middle. */
function orderChildren(children = []) {
  const withTeams = [];
  const leaves = [];

  children.forEach((child) => {
    if (child.children?.length > 0) withTeams.push(child);
    else leaves.push(child);
  });

  const byName = (a, b) => a.name.localeCompare(b.name);
  withTeams.sort(byName);
  leaves.sort(byName);

  if (withTeams.length <= 1) {
    return [...withTeams, ...leaves];
  }

  const mid = Math.ceil(withTeams.length / 2);
  const leftTeams = withTeams.slice(0, mid);
  const rightTeams = withTeams.slice(mid);
  return [...leftTeams, ...leaves, ...rightTeams];
}

function PersonCard({ person, hasTeam, compact = false }) {
  const imageUrl = resolveImageUrl(person.profile_image_url);

  return (
    <div
      className={`ems-org-card ${hasTeam ? "ems-org-card--lead" : ""} ${
        compact ? "ems-org-card--compact" : ""
      }`}
    >
      <div className="ems-org-card-photo">
        {imageUrl ? (
          <img src={imageUrl} alt={person.name} draggable={false} />
        ) : (
          <span className="ems-org-card-initials">{getInitials(person.name)}</span>
        )}
      </div>
      <div className="ems-org-card-meta">
        <div className="ems-org-card-name">{person.name}</div>
        {person.is_shared_resource ? (
          <div className="ems-org-card-code">
            <span className="ems-org-card-shared">Shared Resource</span>
          </div>
        ) : (
          <div className="ems-org-card-code">
            {person.employee_code && person.employee_code !== "-"
              ? person.employee_code
              : person.person_code && person.person_code !== "-"
                ? person.person_code
                : "—"}
          </div>
        )}
        {!person.is_shared_resource &&
        (person.is_freelancer || person.employment_type_name) ? (
          <div className="ems-org-card-code">
            <span
              className={
                person.is_freelancer ||
                normalizeEmploymentType(person.employment_type_name) === "freelancer"
                  ? "ems-org-card-freelancer"
                  : "ems-org-card-employee"
              }
            >
              {person.employment_type_name ||
                (person.is_freelancer ? "Freelancer" : "Employee")}
            </span>
          </div>
        ) : null}
        <div className="ems-org-card-title">{person.designation || "—"}</div>
        {person.level_name && person.level_name !== "Unassigned" ? (
          <div className="ems-org-card-level">{person.level_name}</div>
        ) : null}
      </div>
    </div>
  );
}

function BranchConnector({ index, total }) {
  if (total <= 1) return null;

  const isFirst = index === 0;
  const isLast = index === total - 1;

  return (
    <div className="ems-org-chart-branch-connector" aria-hidden>
      {isFirst ? (
        <div className="ems-org-chart-connector-h ems-org-chart-connector-h--spacer" />
      ) : (
        <div className="ems-org-chart-connector-h ems-org-chart-connector-h--left ems-org-chart-line--dotted" />
      )}
      <div className="ems-org-chart-drop ems-org-chart-line--dotted" />
      {isLast ? (
        <div className="ems-org-chart-connector-h ems-org-chart-connector-h--spacer" />
      ) : (
        <div className="ems-org-chart-connector-h ems-org-chart-connector-h--right ems-org-chart-line--dotted" />
      )}
    </div>
  );
}

function OrgChartNode({ node }) {
  const allChildren = useMemo(
    () => orderChildren(node.children || []),
    [node.children]
  );

  if (allChildren.length === 0) {
    return (
      <div className="ems-org-chart-node">
        <PersonCard person={node} hasTeam={false} />
      </div>
    );
  }

  const teamLeads = allChildren.filter((c) => (c.children?.length ?? 0) > 0);
  const leafReports = allChildren.filter((c) => !(c.children?.length ?? 0));
  const isPureTeam = teamLeads.length === 0 && leafReports.length > 0;
  const leafBranchCount = isPureTeam ? (leafReports.length > 0 ? 1 : 0) : leafReports.length;
  const totalBranches = teamLeads.length + leafBranchCount;
  const useSplitConnectors = totalBranches > 1;

  const branchItems = [];

  teamLeads.forEach((lead) => {
    branchItems.push({
      key: hierarchyNodeKey(lead),
      className: "",
      content: <OrgChartNode node={lead} />,
    });
  });

  if (isPureTeam && leafReports.length > 0) {
    branchItems.push({
      key: `team-${hierarchyNodeKey(node)}`,
      className: "",
      content: (
        <div className="ems-org-team-box-wrap">
          <div className="ems-org-team-box">
            <div
              className={`ems-org-team-box-inner ${
                leafReports.length > 1 ? "ems-org-team-box-inner--pairs" : ""
              }`}
            >
              {leafReports.map((leaf) => (
                <PersonCard
                  key={hierarchyNodeKey(leaf)}
                  person={leaf}
                  hasTeam={false}
                  compact
                />
              ))}
            </div>
          </div>
        </div>
      ),
    });
  } else {
    leafReports.forEach((leaf) => {
      branchItems.push({
        key: hierarchyNodeKey(leaf),
        className: "ems-org-chart-branch-col--leaf",
        content: (
          <div className="ems-org-team-box ems-org-team-box--single">
            <PersonCard person={leaf} hasTeam={false} />
          </div>
        ),
      });
    });
  }

  return (
    <div className="ems-org-chart-node">
      <PersonCard person={node} hasTeam={true} />

      <div className="ems-org-chart-subtree">
        <div className="ems-org-chart-stem ems-org-chart-line--dotted" aria-hidden />

        <div
          className={`ems-org-chart-band ${useSplitConnectors ? "ems-org-chart-band--split" : ""}`}
        >
          {branchItems.map((item, index) => (
            <div
              key={item.key}
              className={`ems-org-chart-branch-col ${item.className}`.trim()}
            >
              {useSplitConnectors ? (
                <BranchConnector index={index} total={branchItems.length} />
              ) : null}
              {item.content}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReportingOrgChart({ tree }) {
  if (!tree?.length) return null;

  return (
    <div className="ems-org-chart-canvas">
      <div className="ems-org-chart-roots">
        {tree.map((root) => (
          <div key={hierarchyNodeKey(root)} className="ems-org-chart-root">
            <OrgChartNode node={root} />
          </div>
        ))}
      </div>
    </div>
  );
}

function OrganizationHierarchy() {
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [personType, setPersonType] = useState("all");

  const loadHierarchy = useCallback(async () => {
    if (!org?.organization_id) {
      setTree([]);
      setEmploymentTypes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await fetchOrganizationHierarchy(org.organization_id);
      setTree(Array.isArray(data.tree) ? data.tree : []);
      setEmploymentTypes(Array.isArray(data.employmentTypes) ? data.employmentTypes : []);
    } catch (error) {
      console.error("Failed to load organization hierarchy:", error);
      toast.error("Failed to load organization hierarchy");
      setTree([]);
      setEmploymentTypes([]);
    } finally {
      setLoading(false);
    }
  }, [org?.organization_id]);

  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);

  const extraEmploymentTypes = useMemo(
    () =>
      (employmentTypes || []).filter((type) => {
        const key = normalizeEmploymentType(type.employment_type_name);
        return key !== "employee" && key !== "freelancer";
      }),
    [employmentTypes]
  );

  const employeeTypeIds = useMemo(
    () => typeIdsForKey(employmentTypes, "employee"),
    [employmentTypes]
  );
  const freelancerTypeIds = useMemo(
    () => typeIdsForKey(employmentTypes, "freelancer"),
    [employmentTypes]
  );

  const filteredTree = useMemo(
    () =>
      filterTreeByPersonType(
        tree,
        personType,
        employeeTypeIds,
        freelancerTypeIds
      ),
    [tree, personType, employeeTypeIds, freelancerTypeIds]
  );

  return (
    <div className="ems-org-hierarchy-page">
      <div className="ems-org-hierarchy-head">
        <h1 className="ems-org-hierarchy-title">
          <Network size={22} strokeWidth={2} />
          Organization Hierarchy
        </h1>
        <label className="ems-org-hierarchy-filter">
          <span>Employment Type</span>
          <select
            value={personType}
            onChange={(event) => setPersonType(event.target.value)}
          >
            <option value="all">All</option>
            <option value="employee">Employee</option>
            <option value="freelancer">Freelancer</option>
            {extraEmploymentTypes.map((type) => (
              <option
                key={type.organization_employment_type_id}
                value={String(type.organization_employment_type_id)}
              >
                {type.employment_type_name}
              </option>
            ))}
            <option value="shared_resource">Shared Resource</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="ems-org-hierarchy-loading">
          <CircularProgress size={28} />
          <span>Loading hierarchy…</span>
        </div>
      ) : tree.length === 0 ? (
        <div className="ems-card ems-org-hierarchy-empty">
          <p>
            No employees found. Set reporting managers on employee profiles to
            build the org chart.
          </p>
        </div>
      ) : filteredTree.length === 0 ? (
        <div className="ems-card ems-org-hierarchy-empty">
          <p>No people found for this employment type.</p>
        </div>
      ) : (
        <div className="ems-card ems-org-hierarchy-chart">
          <ReportingOrgChart tree={filteredTree} />
        </div>
      )}
    </div>
  );
}

export default OrganizationHierarchy;
